import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  Radiation, AlertTriangle, Lock, Globe, Shield, Crosshair,
  TrendingUp, ChevronDown, ChevronUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from "recharts";

interface NuclearState {
  country: string;
  flag: string;
  totalWarheads: number;
  deployed: number;
  reserve: number;
  retired: number;
  firstTest: number;
  icbmRange: string;
  treatyStatus: string;
  alertLevel: "HIGH" | "ELEVATED" | "MODERATE" | "LOW";
  notes: string;
}

// FAS (Federation of American Scientists) public data — 2024/2025 estimates
const NUCLEAR_STATES: NuclearState[] = [
  {
    country: "Russia",
    flag: "🇷🇺",
    totalWarheads: 5580,
    deployed: 1710,
    reserve: 2670,
    retired: 1200,
    firstTest: 1949,
    icbmRange: "16,000+ km",
    treatyStatus: "New START (suspended)",
    alertLevel: "HIGH",
    notes: "Largest arsenal. Suspended New START inspections in 2023. Tactical nuclear doctrine updated.",
  },
  {
    country: "United States",
    flag: "🇺🇸",
    totalWarheads: 5044,
    deployed: 1770,
    reserve: 1938,
    retired: 1336,
    firstTest: 1945,
    icbmRange: "13,000+ km",
    treatyStatus: "New START (suspended)",
    alertLevel: "ELEVATED",
    notes: "Modernizing triad. B-21 Raider entering service. W93 warhead in development.",
  },
  {
    country: "China",
    flag: "🇨🇳",
    totalWarheads: 500,
    deployed: 24,
    reserve: 476,
    retired: 0,
    firstTest: 1964,
    icbmRange: "13,000+ km",
    treatyStatus: "No treaty",
    alertLevel: "ELEVATED",
    notes: "Rapid expansion: 300+ new silos detected. DF-41 ICBM deployed. MIRV capability.",
  },
  {
    country: "France",
    flag: "🇫🇷",
    totalWarheads: 290,
    deployed: 280,
    reserve: 10,
    retired: 0,
    firstTest: 1960,
    icbmRange: "10,000+ km (SLBM)",
    treatyStatus: "NPT member",
    alertLevel: "MODERATE",
    notes: "Sea-based deterrent (SSBN). M51 SLBM. ASMP-A air-launched cruise missile.",
  },
  {
    country: "United Kingdom",
    flag: "🇬🇧",
    totalWarheads: 225,
    deployed: 120,
    reserve: 105,
    retired: 0,
    firstTest: 1952,
    icbmRange: "12,000+ km (SLBM)",
    treatyStatus: "NPT member",
    alertLevel: "MODERATE",
    notes: "Increased cap to 260 warheads. Vanguard-class SSBN fleet. Dreadnought replacement program.",
  },
  {
    country: "Pakistan",
    flag: "🇵🇰",
    totalWarheads: 170,
    deployed: 0,
    reserve: 170,
    retired: 0,
    firstTest: 1998,
    icbmRange: "2,750 km",
    treatyStatus: "Non-NPT",
    alertLevel: "ELEVATED",
    notes: "Fastest growing arsenal. Tactical nukes (Nasr). India rivalry. Shaheen-III extends range.",
  },
  {
    country: "India",
    flag: "🇮🇳",
    totalWarheads: 172,
    deployed: 0,
    reserve: 172,
    retired: 0,
    firstTest: 1974,
    icbmRange: "5,500+ km",
    treatyStatus: "Non-NPT",
    alertLevel: "MODERATE",
    notes: "Agni-V ICBM. SSBN Arihant-class. No-first-use policy. Expanding triad.",
  },
  {
    country: "Israel",
    flag: "🇮🇱",
    totalWarheads: 90,
    deployed: 0,
    reserve: 90,
    retired: 0,
    firstTest: 1979,
    icbmRange: "11,500 km (Jericho III)",
    treatyStatus: "Non-NPT (undeclared)",
    alertLevel: "ELEVATED",
    notes: "Official ambiguity policy. Jericho III ICBM. Dolphin-class SLCM capability. Iran tensions.",
  },
  {
    country: "North Korea",
    flag: "🇰🇵",
    totalWarheads: 50,
    deployed: 0,
    reserve: 50,
    retired: 0,
    firstTest: 2006,
    icbmRange: "15,000+ km (Hwasong-18)",
    treatyStatus: "Withdrew NPT 2003",
    alertLevel: "HIGH",
    notes: "Hwasong-18 solid-fuel ICBM. Tactical nuclear doctrine. Estimated 50+ warheads by 2025.",
  },
];

const ALERT_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  HIGH: { bg: "bg-red-500/15", text: "text-red-400", dot: "bg-red-500" },
  ELEVATED: { bg: "bg-orange-500/15", text: "text-orange-400", dot: "bg-orange-500" },
  MODERATE: { bg: "bg-yellow-500/15", text: "text-yellow-400", dot: "bg-yellow-500" },
  LOW: { bg: "bg-green-500/15", text: "text-green-400", dot: "bg-green-500" },
};

const BAR_COLORS = [
  "hsl(0 70% 50%)",    // Russia - red
  "hsl(220 70% 55%)",  // US - blue
  "hsl(45 80% 50%)",   // China - gold
  "hsl(240 50% 55%)",  // France - purple
  "hsl(200 60% 50%)",  // UK - teal
  "hsl(120 50% 45%)",  // Pakistan - green
  "hsl(30 80% 50%)",   // India - orange
  "hsl(210 70% 60%)",  // Israel - light blue
  "hsl(350 60% 45%)",  // NK - dark red
];

function formatNumber(n: number): string {
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export default function NuclearThreatMonitor() {
  const { isPremium } = useAuth();
  const [expanded, setExpanded] = useState(false);

  const totalGlobal = NUCLEAR_STATES.reduce((s, n) => s + n.totalWarheads, 0);
  const totalDeployed = NUCLEAR_STATES.reduce((s, n) => s + n.deployed, 0);

  const barData = NUCLEAR_STATES.map((n) => ({
    name: n.flag + " " + (n.country.length > 10 ? n.country.slice(0, 10) + "…" : n.country),
    Deployed: n.deployed,
    Reserve: n.reserve,
    Retired: n.retired,
  }));

  const pieData = NUCLEAR_STATES.map((n) => ({
    name: n.country,
    value: n.totalWarheads,
  }));

  const highAlertCount = NUCLEAR_STATES.filter((n) => n.alertLevel === "HIGH").length;
  const visibleStates = expanded ? NUCLEAR_STATES : NUCLEAR_STATES.slice(0, isPremium ? 9 : 5);

  return (
    <section className="py-8 border-t border-primary/10">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-red-500/10">
            <Radiation className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Nuclear Threat Monitor</h2>
            <p className="text-xs text-muted-foreground">
              Global nuclear arsenals — FAS estimates (2024/2025)
            </p>
          </div>
          {highAlertCount > 0 && (
            <Badge variant="outline" className="ml-auto text-[10px] bg-red-500/10 text-red-400 border-red-500/30 animate-pulse">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {highAlertCount} HIGH ALERT
            </Badge>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="rounded-lg border border-primary/10 bg-card/50 p-3 text-center">
            <Radiation className="h-4 w-4 mx-auto mb-1 text-red-400" />
            <p className="text-2xl font-bold font-mono text-foreground">{formatNumber(totalGlobal)}</p>
            <p className="text-[10px] text-muted-foreground">Total warheads</p>
          </div>
          <div className="rounded-lg border border-primary/10 bg-card/50 p-3 text-center">
            <Crosshair className="h-4 w-4 mx-auto mb-1 text-orange-400" />
            <p className="text-2xl font-bold font-mono text-foreground">{formatNumber(totalDeployed)}</p>
            <p className="text-[10px] text-muted-foreground">Deployed</p>
          </div>
          <div className="rounded-lg border border-primary/10 bg-card/50 p-3 text-center">
            <Globe className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
            <p className="text-2xl font-bold font-mono text-foreground">{NUCLEAR_STATES.length}</p>
            <p className="text-[10px] text-muted-foreground">Nuclear states</p>
          </div>
          <div className="rounded-lg border border-primary/10 bg-card/50 p-3 text-center">
            <Shield className="h-4 w-4 mx-auto mb-1 text-yellow-400" />
            <p className="text-2xl font-bold font-mono text-foreground">
              {NUCLEAR_STATES.filter((n) => n.alertLevel === "HIGH" || n.alertLevel === "ELEVATED").length}
            </p>
            <p className="text-[10px] text-muted-foreground">Elevated+ alert</p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Bar chart — Arsenal breakdown */}
          <div className="rounded-lg border border-primary/10 bg-card/50 p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Arsenal Breakdown</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} layout="vertical" margin={{ left: 95, right: 10, top: 5, bottom: 5 }}>
                  <XAxis type="number" tickFormatter={formatNumber} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                  <Tooltip formatter={(v: number) => formatNumber(v)} />
                  <Bar dataKey="Deployed" stackId="a" fill="hsl(0 70% 50%)" />
                  <Bar dataKey="Reserve" stackId="a" fill="hsl(var(--primary) / 0.6)" />
                  <Bar dataKey="Retired" stackId="a" fill="hsl(var(--muted-foreground) / 0.3)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-4 justify-center mt-2 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "hsl(0 70% 50%)" }} /> Deployed</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary/60" /> Reserve</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-muted-foreground/30" /> Retired</span>
            </div>
          </div>

          {/* Pie chart — Share */}
          <div className="rounded-lg border border-primary/10 bg-card/50 p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Global Arsenal Share</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius="70%"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                    fontSize={9}
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => formatNumber(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Alert status grid */}
        <div className="rounded-lg border border-primary/10 bg-card/50 p-4 mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" /> Threat Alert Levels
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {NUCLEAR_STATES.map((n) => {
              const c = ALERT_COLORS[n.alertLevel];
              return (
                <div key={n.country} className={`rounded-md border border-primary/10 p-2.5 ${c.bg}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-lg">{n.flag}</span>
                    <span className="text-xs font-semibold text-foreground">{n.country}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`h-1.5 w-1.5 rounded-full ${c.dot} ${n.alertLevel === "HIGH" ? "animate-pulse" : ""}`} />
                    <span className={`text-[10px] font-mono font-bold ${c.text}`}>{n.alertLevel}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">{formatNumber(n.totalWarheads)} warheads</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail table */}
        <div className="rounded-lg border border-primary/10 bg-card/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-primary/10 bg-muted/30">
                  <th className="text-left px-3 py-2 font-semibold text-foreground">Country</th>
                  <th className="text-right px-3 py-2 font-semibold text-foreground">Total</th>
                  <th className="text-right px-3 py-2 font-semibold text-foreground">Deployed</th>
                  <th className="text-right px-3 py-2 font-semibold text-foreground">ICBM Range</th>
                  <th className="text-center px-3 py-2 font-semibold text-foreground">First Test</th>
                  <th className="text-center px-3 py-2 font-semibold text-foreground">Alert</th>
                  <th className="text-left px-3 py-2 font-semibold text-foreground">Treaty</th>
                </tr>
              </thead>
              <tbody>
                {visibleStates.map((n) => {
                  const c = ALERT_COLORS[n.alertLevel];
                  return (
                    <tr key={n.country} className="border-b border-primary/5 hover:bg-muted/20">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span>{n.flag}</span>
                          <span className="font-medium text-foreground">{n.country}</span>
                        </div>
                      </td>
                      <td className="text-right px-3 py-2 font-mono font-bold text-foreground">{formatNumber(n.totalWarheads)}</td>
                      <td className="text-right px-3 py-2 font-mono text-muted-foreground">{formatNumber(n.deployed)}</td>
                      <td className="text-right px-3 py-2 font-mono text-muted-foreground">{n.icbmRange}</td>
                      <td className="text-center px-3 py-2 font-mono text-muted-foreground">{n.firstTest}</td>
                      <td className="text-center px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${c.bg} ${c.text}`}>
                          {n.alertLevel}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{n.treatyStatus}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Premium gate */}
          {!isPremium && NUCLEAR_STATES.length > 5 && (
            <div className="flex items-center justify-center gap-2 py-4 bg-muted/10 border-t border-primary/10">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                Upgrade to Premium for full arsenal details, notes & doctrine analysis
              </p>
            </div>
          )}

          {isPremium && !expanded && NUCLEAR_STATES.length > 9 && (
            <button
              onClick={() => setExpanded(true)}
              className="w-full flex items-center justify-center gap-1 py-2 text-xs text-primary hover:bg-muted/10 border-t border-primary/10"
            >
              <ChevronDown className="h-3 w-3" /> Show all
            </button>
          )}
        </div>

        {/* Notes for premium users */}
        {isPremium && (
          <div className="mt-4 rounded-lg border border-primary/10 bg-card/50 p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">📋 Intelligence Notes</h3>
            <div className="space-y-2">
              {NUCLEAR_STATES.map((n) => (
                <div key={n.country} className="flex gap-2 text-xs">
                  <span className="font-semibold text-foreground whitespace-nowrap">{n.flag} {n.country}:</span>
                  <span className="text-muted-foreground">{n.notes}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground mt-3">
          Source: Federation of American Scientists (FAS) Nuclear Notebook, SIPRI Yearbook. Estimates as of early 2025.
        </p>
      </div>
    </section>
  );
}
